import { type ReactNode, useRef } from 'react'

import { Container } from '../../../components/Container'
import { CHAT_SCROLLER_ATTR } from '../../../hooks/useChatAutoScroll'
import { useChatHoldHeight } from '../../../hooks/useChatHoldHeight'
import { useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade'
import { cn } from '../../../lib/utils'
import {
  type ApplicationStep,
  InsuranceApplicationProgress,
  InsuranceApplicationProgressBar,
  InsuranceApplicationStepLine,
} from './InsuranceApplicationProgress'

interface ApplicationChatLayoutProps {
  steps: ReadonlyArray<ApplicationStep>
  /** 0–100, from the backend; fills the hairline across the top. */
  percentComplete: number
  /** The indication details sheet is open over the right edge, so the chat centres in what is left. */
  sidebarOpen?: boolean
  /** Staff boards sit in their own page frame, so this one adds no gutter of its own. */
  staffView?: boolean
  /** A line across the top saying what Cai is working on. */
  status?: ReactNode
  /** Pinned under the conversation, outside its scroll. */
  footer?: ReactNode
  children: ReactNode
}

/** Progress hairline, steps down the left, and the chat scrolling in its own centred column. */
export function ApplicationChatLayout({
  steps,
  percentComplete,
  sidebarOpen,
  staffView,
  status,
  footer,
  children,
}: ApplicationChatLayoutProps) {
  const scroller = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const spacer = useRef<HTMLDivElement>(null)
  const fade = useScrollEdgeFade(scroller)
  useChatHoldHeight(scroller, content, spacer)
  const Frame = staffView ? 'div' : Container

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <InsuranceApplicationProgressBar percent={percentComplete} />
      {/* Sized by its container rather than the viewport: staff boards render this beside a nav. */}
      <Frame className="@container relative flex min-h-0 flex-1 flex-col">
        <InsuranceApplicationProgress
          steps={steps}
          className={cn('absolute top-12 left-4 hidden w-50', !sidebarOpen && '@min-[64rem]:flex')}
        />
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col transition-[padding] duration-500',
            sidebarOpen && 'lg:pr-125'
          )}
        >
          <InsuranceApplicationStepLine
            steps={steps}
            className={cn(
              'mx-auto w-full max-w-130 pt-4 @min-[48rem]:max-w-none',
              !sidebarOpen && '@min-[64rem]:hidden'
            )}
          />
          {/* Under the step line it overlays the chat's faded top edge instead of taking a row. */}
          <div className={cn('relative z-10 h-0 shrink-0', !sidebarOpen && '@min-[64rem]:h-auto')}>
            {status}
          </div>
          <div
            ref={scroller}
            {...{ [CHAT_SCROLLER_ATTR]: '' }}
            style={fade}
            // relative: absolute descendants (sr-only live regions) otherwise escape the scroll and stretch the page.
            className="no-scrollbar relative min-h-0 flex-1 overflow-y-auto"
          >
            {/* A new chat starts at the top and grows down. */}
            <div className={cn('mx-auto flex w-full max-w-130 flex-col pt-6', footer ? 'pb-4' : 'pb-10')}>
              <div ref={content}>{children}</div>
              <div ref={spacer} aria-hidden className="shrink-0" />
            </div>
          </div>
          {footer && <div className="mx-auto w-full max-w-130 shrink-0 pb-4">{footer}</div>}
        </div>
      </Frame>
    </div>
  )
}
